import { useServices } from "@/services/hooks/useServices";
import { TemplateCheckResponse, TemplateType } from "@/types/template.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useTemplates = (
  page: number,
  enabled: boolean,
  limit: number = 10,
  labels?: string[]
) => {
  const { templateService } = useServices();

  return useQuery({
    queryKey: ["templates", { page, limit, labels }],
    queryFn: () => templateService.getAll({ page, limit, labels }),
    enabled: enabled ?? true,
  });
};

export const useSearchTemplates = (searchTerm?: string) => {
  const { templateService } = useServices();
  return useQuery({
    queryKey: ["templates", "search", searchTerm],
    queryFn: () => templateService.search(searchTerm),
  });
};

export const useAllCompanyTemplates = () => {
  const { templateService } = useServices();
  return useQuery({
    queryKey: ["templates", "all-company"],
    queryFn: () => templateService.getAllCompanyTemplates(),
  });
};

export const useGetTemplate = ({
  id,
  enabled = true,
}: {
  id: string;
  enabled?: boolean;
}) => {
  const { templateService } = useServices();

  return useQuery({
    queryKey: ["templates", id],
    queryFn: () => templateService.getById(id),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTemplate = () => {
  const { templateService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateData,
      report,
    }: {
      templateData: Partial<TemplateType>;
      report?: TemplateCheckResponse;
    }) => templateService.create({ templateData, report }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useUpdateTemplate = () => {
  const { templateService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      templateData,
    }: {
      id: string;
      templateData: Partial<TemplateType>;
    }) => templateService.update(id, templateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useDeleteTemplate = () => {
  const { templateService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useCheckTemplate = () => {
  const { templateService } = useServices();
  return useMutation({
    mutationFn: (
      templateData: Pick<
        TemplateType,
        "content" | "contentType" | "subject"
      > & {
        _id?: string;
      }
    ) => templateService.check(templateData),
  });
};

export const useGetTemplateReport = ({ id }: { id: string }) => {
  const { templateService } = useServices();
  return useQuery({
    queryKey: ["templates", id, "report"],
    queryFn: () => templateService.getReport(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useAddLabelsToTemplates = () => {
  const { templateService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateIds,
      labelIds,
    }: {
      templateIds: string[];
      labelIds: string[];
    }) => templateService.addLabelsToTemplates(templateIds, labelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useRemoveLabelsFromTemplates = () => {
  const { templateService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateIds,
      labelIds
    }: {
      templateIds: string[];
      labelIds: string[];
    }) => templateService.removeLabelsFromTemplates(templateIds, labelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};
